import { createAction, props } from '@ngrx/store';
import { 
  RuleDefinition, 
  RuleCategory, 
  RuleTemplate, 
  TestRuleResponse 
} from '@core/models/api/api-models';
import { RuleType, RuleConfiguration } from '@core/models/rule-configuration.models';
import { WizardStep } from './rule-builder.state';

/**
 * Rule Builder Actions (6-Step Wizard)
 */

// ============================================================================
// Initialization & Reference Data
// ============================================================================

export const initializeWizard = createAction(
  '[Rule Builder] Initialize Wizard'
);

export const initializeWizardFailure = createAction(
  '[Rule Builder] Initialize Wizard Failure',
  props<{ error: string }>()
);

export const loadRuleForEdit = createAction(
  '[Rule Builder] Load Rule For Edit',
  props<{ ruleId: string }>()
);

export const loadRuleSuccess = createAction(
  '[Rule Builder] Load Rule Success',
  props<{ rule: RuleDefinition }>()
);

export const loadRuleFailure = createAction(
  '[Rule Builder] Load Rule Failure',
  props<{ error: string }>()
);

export const loadCategories = createAction(
  '[Rule Builder] Load Categories'
);

export const loadCategoriesSuccess = createAction(
  '[Rule Builder] Load Categories Success',
  props<{ categories: RuleCategory[] }>()
);

export const loadCategoriesFailure = createAction(
  '[Rule Builder] Load Categories Failure',
  props<{ error: string }>()
);

export const loadTemplates = createAction(
  '[Rule Builder] Load Templates',
  props<{ ruleType?: RuleType }>()
);

export const loadTemplatesSuccess = createAction(
  '[Rule Builder] Load Templates Success',
  props<{ templates: RuleTemplate[] }>()
);

export const loadTemplatesFailure = createAction(
  '[Rule Builder] Load Templates Failure',
  props<{ error: string }>()
);

// ============================================================================
// Step 1: Choose Rule Type
// ============================================================================

export const selectRuleType = createAction(
  '[Rule Builder] Select Rule Type',
  props<{ ruleType: RuleType }>()
);

export const selectTemplate = createAction(
  '[Rule Builder] Select Template',
  props<{ templateId: string }>()
);

export const clearTemplate = createAction(
  '[Rule Builder] Clear Template'
);

export const createRuleFromTemplate = createAction(
  '[Rule Builder] Create Rule From Template',
  props<{ templateId: string }>()
);

export const createRuleFromTemplateSuccess = createAction(
  '[Rule Builder] Create Rule From Template Success',
  props<{ rule: Partial<RuleDefinition> }>()
);

export const createRuleFromTemplateFailure = createAction(
  '[Rule Builder] Create Rule From Template Failure',
  props<{ error: string }>()
);

export const loadTemplateDetails = createAction(
  '[Rule Builder] Load Template Details',
  props<{ templateId: string }>()
);

export const loadTemplateDetailsSuccess = createAction(
  '[Rule Builder] Load Template Details Success',
  props<{ template: RuleTemplate }>()
);

export const loadTemplateDetailsFailure = createAction(
  '[Rule Builder] Load Template Details Failure',
  props<{ error: string }>()
);

// ============================================================================
// Step 2: Category & Basic Info
// ============================================================================

export const selectCategory = createAction(
  '[Rule Builder] Select Category',
  props<{ categoryId: string }>()
);

export const updateCategoryId = createAction(
  '[Rule Builder] Update Category ID',
  props<{ categoryId: string | null }>()
);

export const updateRuleName = createAction(
  '[Rule Builder] Update Rule Name',
  props<{ name: string }>()
);

export const updateRuleDescription = createAction(
  '[Rule Builder] Update Rule Description',
  props<{ description: string }>()
);

export const updateTargetSheets = createAction(
  '[Rule Builder] Update Target Sheets',
  props<{ targetSheets: string[] }>()
);

// ============================================================================
// Step 3: Configure Parameters
// ============================================================================

export const updateConfiguration = createAction(
  '[Rule Builder] Update Configuration',
  props<{ configuration: RuleConfiguration }>()
);

export const parseFormula = createAction(
  '[Rule Builder] Parse Formula',
  props<{ formula: string }>()
);

export const parseFormulaSuccess = createAction(
  '[Rule Builder] Parse Formula Success',
  props<{ sources: Array<{ sheet: string; cell: string }> }>()
);

export const parseFormulaFailure = createAction(
  '[Rule Builder] Parse Formula Failure',
  props<{ error: string }>()
);

// ============================================================================
// Step 4: Test Rule
// ============================================================================

export const uploadTestFile = createAction(
  '[Rule Builder] Upload Test File',
  props<{ file: File }>()
);

export const testRuleSuccess = createAction(
  '[Rule Builder] Test Rule Success',
  props<{ results: TestRuleResponse }>()
);

export const testRuleFailure = createAction(
  '[Rule Builder] Test Rule Failure',
  props<{ error: string }>()
);

export const clearTestResults = createAction(
  '[Rule Builder] Clear Test Results'
);

// ============================================================================
// Step 5: Review
// ============================================================================

export const markAsReviewed = createAction(
  '[Rule Builder] Mark As Reviewed'
);

// ============================================================================
// Step 6: Save & Activate
// ============================================================================

export const updateIsActive = createAction(
  '[Rule Builder] Update Is Active',
  props<{ isActive: boolean }>()
);

export const updateDisplayOrder = createAction(
  '[Rule Builder] Update Display Order',
  props<{ displayOrder: number | null }>()
);

export const toggleActivateImmediately = createAction(
  '[Rule Builder] Toggle Activate Immediately',
  props<{ activate: boolean }>()
);

export const toggleSaveAsTemplate = createAction(
  '[Rule Builder] Toggle Save As Template',
  props<{ saveAsTemplate: boolean }>()
);

export const saveRule = createAction(
  '[Rule Builder] Save Rule'
);

export const saveRuleSuccess = createAction(
  '[Rule Builder] Save Rule Success',
  props<{ rule: RuleDefinition }>()
);

export const saveRuleFailure = createAction(
  '[Rule Builder] Save Rule Failure',
  props<{ error: string }>()
);

// ============================================================================
// Navigation
// ============================================================================

export const goToStep = createAction(
  '[Rule Builder] Go To Step',
  props<{ step: WizardStep }>()
);

export const nextStep = createAction(
  '[Rule Builder] Next Step'
);

export const previousStep = createAction(
  '[Rule Builder] Previous Step'
);

export const jumpToStep = createAction(
  '[Rule Builder] Jump To Step',
  props<{ step: WizardStep }>()
);

// ============================================================================
// Validation & Errors
// ============================================================================

export const validateCurrentStep = createAction(
  '[Rule Builder] Validate Current Step'
);

export const setValidationErrors = createAction(
  '[Rule Builder] Set Validation Errors',
  props<{ errors: Record<string, string> }>()
);

export const clearValidationErrors = createAction(
  '[Rule Builder] Clear Validation Errors'
);

export const setError = createAction(
  '[Rule Builder] Set Error',
  props<{ error: string }>()
);

export const clearError = createAction(
  '[Rule Builder] Clear Error'
);

// ============================================================================
// Utility
// ============================================================================

export const resetWizard = createAction(
  '[Rule Builder] Reset Wizard'
);

export const cancelWizard = createAction(
  '[Rule Builder] Cancel Wizard'
);

export const setDirty = createAction(
  '[Rule Builder] Set Dirty',
  props<{ isDirty: boolean }>()
);
